import { theme } from '@theme';
import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDealStatus, useOutsideClickDetection } from '../../../../../hooks';
import { useCheckDevice } from '../../../../../hooks/UseCheckDevice';
import { FilterItemResource, FilterStatusItemType } from '../../../../../types/FilterTypes';
import SingleSelectDropdownOptions from '../../../../shared/filters/single-select-dropdown/SingleSelectDropdownOptions';
import {
  Icon,
  NumberBadge,
  OptionItem,
  OptionItemInput,
  OptionItemLabel,
  Typography,
} from '../../../../style';

type PortfolioStatusSingleSelectDropdownProps = {
  item: FilterStatusItemType;
  options: readonly FilterItemResource[];
  value?: FilterItemResource;
  onChange: (value: FilterItemResource) => void;
  onOpen?: () => void;
  onClose?: () => void;
  checked: boolean;
};

const PortfolioStatusSingleSelectDropdown: FC<PortfolioStatusSingleSelectDropdownProps> = ({
  item,
  options,
  value,
  onChange,
  checked,
  onOpen,
  onClose,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [selectedValue, setSelectedValue] = useState<FilterItemResource>({
    key: null,
    label: 'All',
  });
  const [filterHeight, setFilterHeight] = useState<number>(0);
  const {
    getFilterCountBackgroundColor: getFlterCountBackgroundColor,
    getFilterCountColor: getFlterCountColor,
  } = useDealStatus();
  const { isMobileDevice } = useCheckDevice();

  useOutsideClickDetection(wrapperRef, setIsVisible);

  const isSelected = (option: FilterItemResource) => {
    return selectedValue?.key === option.key;
  };

  const handleItemChange = (option: FilterItemResource): void => {
    if (value?.key !== option.key) {
      onChange({ ...option });
      setIsVisible(!isVisible);
    }
  };

  const handleMouseEvent = (event: any): void => {
    if (event && event.target) {
      setFilterHeight(event.target.offsetParent?.offsetHeight || 0);
    }
  };

  const generateOptions = (): JSX.Element[] => {
    return options?.map((option) => (
      <OptionItem
        selected={option.key === value?.key}
        key={option.key}
        onClick={() => handleItemChange(option)}
      >
        <span className='align-item'>
          <OptionItemInput
            type='radio'
            top={2}
            checked={isSelected(option)}
            onChange={() => handleItemChange(option)}
          />
          <OptionItemLabel>{option.label}</OptionItemLabel>
        </span>
        <span className='filter-right'>{<NumberBadge>{option.count || 0}</NumberBadge>}</span>
      </OptionItem>
    ));
  };

  const getLabel = (): string => {
    return checked && item.key ? selectedValue?.label : null;
  };

  const handleDropdownWrapperClick = (): void => {
    setIsVisible(!isVisible);

    if (!options || !options.length) {
      onChange(null);
    }
  };

  useEffect(() => {
    setSelectedValue(value);
    setTimeout(() => {
      setFilterHeight(headerRef?.current?.getBoundingClientRect()?.height || 0);
    }, 30);
  }, [value]);

  useEffect(() => {
    if (isVisible && onOpen) {
      onOpen();
    }
    if (!isVisible && onClose) {
      onClose();
    }
  }, [isVisible]);

  return (
    <PortfolioSingleSelectDropdown
      data-testid='portfolio-single-select-dropdown'
      className='portfolio-status-single-select'
      isMobile={isMobileDevice}
      ref={wrapperRef}
    >
      <div className='heap-filter-item w-88'>
        <span
          data-testid='portfolio-single-select-dropdown-header'
          ref={headerRef}
          onClick={handleDropdownWrapperClick}
          onMouseDown={handleMouseEvent}
        >
          <Select data-testid='dropdown-select'>
            <div>
              <span className='select-radio'>
                <OptionItemInput
                  type='radio'
                  top={5}
                  onChange={() => {
                    onChange({ key: null, label: 'All' });
                  }}
                  checked={checked}
                />
                <div>
                  <Typography
                    display='block'
                    theme={theme}
                    marginTop={5}
                    size={theme.typography.h5}
                    fontWeight={theme.typography.fontWeights.semibold}
                  >
                    {item.label}
                  </Typography>
                  <Typography data-testid='dropdown-select-bottom-label' size={theme.typography.h6}>
                    {getLabel()}
                  </Typography>
                </div>
              </span>

              <div className='filter-count-badge'>
                <NumberBadge
                  backgroundColor={getFlterCountBackgroundColor(item.key)}
                  color={getFlterCountColor(item.key)}
                  onClick={handleDropdownWrapperClick}
                >
                  {item.count || 0}
                </NumberBadge>
              </div>
            </div>
          </Select>
        </span>
        {isVisible && options?.length && (
          <SingleSelectDropdownOptions options={generateOptions()} filterHeight={filterHeight} />
        )}
      </div>
      <div className='vertical-center status-icon'>
        <Icon type='dropdown' size={14} />
      </div>
    </PortfolioSingleSelectDropdown>
  );
};

export default PortfolioStatusSingleSelectDropdown;

type PortfolioSingleSelectDropdownProps = {
  isMobile?: boolean;
};

const PortfolioSingleSelectDropdown = styled.div<PortfolioSingleSelectDropdownProps>`
  justify-content: ${({ isMobile }) => (isMobile ? 'space-around' : 'flex-start')};
`;

const Select = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0px;
  margin-left: 10px;
  line-height: 20px;
  & div:first-child {
    display: flex;
    justify-content: space-between;

    & .filter-count-badge {
      display: flex;
      align-items: center;

      & .badge {
        margin-top: 2px;
      }
    }
  }

  .select-radio {
    display: inline-flex;
    align-items: start;
    width: 75%;
  }
`;
